/**
 * Recording a voice note in the composer: the microphone, then the take as a file. A voice note has no time limit.
 * The recorder hands over a part every second, so a long take is many small blobs the browser manages, not one
 * growing buffer; the upload cuts the finished file into parts as any note's does (`recordNote`, the composer's
 * part). A video note is recorded in the shared video recorder's sheet and arrives as a `Take` too.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { NoteKind } from "../../shared/types";
import type { LiveNoteUpload } from "../lib/liveNoteUpload";
import { NOTE_MAX_SEC } from "../lib/noteLimits";

const AUDIO_MIME = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

function pickMime(candidates: string[]): string {
  if (typeof MediaRecorder === "undefined") return "";
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
}

/** A finished take, before it is uploaded. */
export interface Take {
  kind: NoteKind;
  blob: Blob;
  seconds: number;
  /** A local address that plays the take at once. */
  preview: string;
  /** The video take's upload that began while it was recorded; its parts are already up. */
  live?: LiveNoteUpload | null;
}

export interface Recording {
  seconds: number;
}

/** Why a recording could not start, in plain words. */
function recordError(err: unknown): string {
  const denied = err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "SecurityError");
  return denied ? "Allow the microphone to record a voice note." : "We couldn't open the microphone.";
}

/** One take: its recorder and stream, what it has recorded, its clock, and whether it is kept when it stops. */
interface Session {
  recorder: MediaRecorder;
  stream: MediaStream;
  chunks: Blob[];
  started: number;
  tick: number | undefined;
  keep: boolean;
}

const closeTracks = (stream: MediaStream) => {
  for (const t of stream.getTracks()) t.stop();
};

export function useNoteRecorder(onTake: (take: Take) => void, onError: (message: string) => void) {
  const [rec, setRec] = useState<Recording | null>(null);
  const current = useRef<Session | null>(null);
  // Why a count: a start waits for the camera, and the person can leave before it opens; that start must not record.
  const gen = useRef(0);
  const take = useRef(onTake);
  take.current = onTake;

  const stop = useCallback((keepIt: boolean) => {
    gen.current += 1;
    const s = current.current;
    if (!s) return;
    current.current = null;
    s.keep = keepIt;
    window.clearInterval(s.tick);
    setRec(null);
    if (s.recorder.state !== "inactive") s.recorder.stop();
    // Leaving frees the camera and microphone at once; a kept take lets the recorder hand over its last part first.
    if (!keepIt) closeTracks(s.stream);
  }, []);

  const start = useCallback(
    async () => {
      stop(false);
      const mine = gen.current;
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        if (mine === gen.current) onError(recordError(err));
        return;
      }
      if (mine !== gen.current) {
        closeTracks(stream);
        return;
      }
      const mime = pickMime(AUDIO_MIME);
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const s: Session = { recorder, stream, chunks: [], started: Date.now(), tick: undefined, keep: false };
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) s.chunks.push(e.data);
      };
      recorder.onstop = () => {
        const seconds = Math.max(1, Math.round((Date.now() - s.started) / 1000));
        closeTracks(s.stream);
        const blob = new Blob(s.chunks, { type: recorder.mimeType || mime || "audio/webm" });
        if (s.keep && blob.size > 0) take.current({ kind: "voice", blob, seconds: Math.min(seconds, NOTE_MAX_SEC.voice ?? seconds), preview: URL.createObjectURL(blob) });
      };
      current.current = s;
      recorder.start(1000);
      s.started = Date.now();
      setRec({ seconds: 0 });
      // The clock reads the time that has passed, so a tick the browser holds back never slows it down.
      s.tick = window.setInterval(() => {
        const sec = Math.floor((Date.now() - s.started) / 1000);
        if (sec >= (NOTE_MAX_SEC.voice ?? Number.POSITIVE_INFINITY)) stop(true);
        else setRec((cur) => (cur && cur.seconds !== sec ? { ...cur, seconds: sec } : cur));
      }, 250);
    },
    [onError, stop],
  );

  const done = useCallback(() => stop(true), [stop]);
  const cancel = useCallback(() => stop(false), [stop]);

  useEffect(() => () => stop(false), [stop]);

  return { rec, start, done, cancel };
}
